import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemDecorator,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { ApiUserInline } from "griptonite-api-client";
import sortBy from "lodash/sortBy";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useMeProfileQuery } from "@/hooks/profile";

import { useCurrentVenueUuid, useVenueStaffQuery } from "../../hooks/venues";
import Avatar from "../Avatar";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

export const StaffItem = ({
  staffInfo,
  showName = true,
  nameOverride,
}: {
  staffInfo?: { user: ApiUserInline };
  showName?: boolean;
  nameOverride?: string;
}) => {
  return (
    <ListItemDecorator>
      <Box sx={{ marginRight: showName ? 1 : 0 }}>
        {/* <Avatar
          size="sm"
          src={staffInfo?.user.pictureUrl}
          alt={staffInfo?.user.firstName}
        /> */}
        <Avatar user={staffInfo?.user} round size={24} />
      </Box>
      <Typography fontWeight="bold" fontSize={14} sx={{ display: showName ? "block" : "none" }}>
        {nameOverride ? nameOverride : `${staffInfo?.user.firstName} ${staffInfo?.user.lastName}`}
      </Typography>
    </ListItemDecorator>
  );
};

type Props<T extends boolean | undefined> = {
  name: string;
  label: string;
  multiple?: T;
  onlyActiveStaff?: boolean;
};

type ValueType<T> = T extends true ? string[] : string | null;

export default function StaffSelect<T extends boolean | undefined = undefined>({
  name,
  label,
  multiple,
  onlyActiveStaff,
}: Props<T>) {
  const { control } = useFormContext();
  const { data: meProfile } = useMeProfileQuery();
  const { field, fieldState } = useController({
    name,
    control,
  });

  const currentVenueUuid = useCurrentVenueUuid();
  let { data: staff } = useVenueStaffQuery(currentVenueUuid, (staff) =>
    onlyActiveStaff ? staff.filter((staffInfo) => staffInfo.isActive) : staff,
  );

  staff = useMemo(() => {
    return sortBy(
      staff,
      (staffInfo) => (staffInfo.user.uuid === meProfile?.uuid ? 0 : 1),
      (staffInfo) => staffInfo.user.firstName + " " + staffInfo.user.lastName,
    );
  }, [staff, meProfile]);

  return (
    <FormControl error={Boolean(fieldState.error)}>
      <FormLabel>{label}</FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      <Select
        slotProps={{
          button: {
            sx: {
              flexWrap: "wrap",
              gap: 1,
            },
          },
        }}
        multiple={multiple}
        value={field.value}
        onChange={(_, value) => field.onChange(value as unknown as ValueType<T>)}
        onBlur={field.onBlur}
        renderValue={(value) =>
          Array.isArray(value) ? (
            value.map((option) => (
              <StaffItem
                key={option.value}
                staffInfo={staff?.find((staffInfo) => staffInfo.user.uuid === option.value)}
              />
            ))
          ) : (
            <StaffItem
              staffInfo={staff?.find((staffInfo) => staffInfo.user.uuid === value?.value)}
            />
          )
        }
      >
        {staff?.map((staffInfo) => (
          <Option
            key={staffInfo.user.uuid}
            value={staffInfo.user.uuid}
            label={`${staffInfo.user.firstName} ${staffInfo.user.lastName}`}
          >
            <StaffItem
              staffInfo={staffInfo}
              key={staffInfo.user.uuid}
              nameOverride={meProfile?.uuid === staffInfo.user.uuid ? "Me" : undefined}
            />
          </Option>
        ))}
      </Select>
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormControl>
  );
}
